import { Button } from 'antd';
import { FC, useEffect, useState } from 'react';
import { EasyField } from 'react-antd-formutil';
import { useNavigate } from 'react-router';
import Card, { CardButtonWrap } from 'components/Card';
import InfoTag from 'components/InfoTag';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import SymbolPreviewList from 'modules/Transfer/components/SymbolPreviewList';
import store from 'modules/Transfer/store';
import { SymbolTypeEnum } from 'modules/Transfer/type';
import { ReactComponent as IconAdd } from 'static/images/icons/add.svg';
import { ReactComponent as IconDelete } from 'static/images/icons/delete.svg';
import { ReactComponent as IconEdit } from 'static/images/icons/edit.svg';
import classlist from 'utils/classlist';
import { formatMoney } from 'utils/format';
import { isEmptyValue } from 'utils/helper';
import withTransferForm, { PageIdEnum, TransferFormProps } from '../../../withTransferForm';
import { PARTIAL_ACATS_BASE_PATH } from '../helper';
import { PartialDetailsPageFormutil } from '../type';
import { normalizeTicker, SYMBOL_TYPE_ROUTES } from './helper';
import style from './style.scss';

const PartialDetails: FC<TransferFormProps<PartialDetailsPageFormutil>> = ({
    $formutil,
    onSubmit,
    goNextPath,
    setStorageDataByKey
}) => {
    const { $params, $setValues } = $formutil;
    const { isEditMode } = store;
    const { temporaryCash, temporaryStockList = [], temporaryOptionList = [], temporaryFundList = [] } = $params;
    const [filterType, setFilterType] = useState('all');
    const navigate = useNavigate();
    // cash值非空, 就代表有值
    const hasCash = !isEmptyValue(temporaryCash);
    const hasStock = temporaryStockList.length > 0;
    const hasOption = temporaryOptionList.length > 0;
    const hasFund = temporaryFundList.length > 0;
    // 是否展示顶部的过滤标签
    const showFilter = [hasCash, hasStock, hasOption, hasFund].filter(Boolean).length > 1;

    useEffect(() => {
        if (
            !showFilter ||
            (filterType === 'cash' && !hasCash) ||
            (filterType === 'stock' && !hasStock) ||
            (filterType === 'option' && !hasOption) ||
            (filterType === 'fund' && !hasFund)
        ) {
            setFilterType('all');
        }
    }, [hasCash, hasStock, hasOption, hasFund, showFilter, filterType]);

    const handleAddCash = (isEdit: boolean) => {
        if (isEdit) {
            navigate('../fill-cash', {
                state: {
                    isEdit: true
                }
            });
        } else {
            navigate('../fill-cash');
        }
    };
    const handleDeleteCash = async () => {
        // 需要把temporaryCash设置成默认值, 就代表了清空现金
        await $setValues(
            {
                temporaryCash: ''
            },
            $innerFormutil => {
                // 同时还要清空当前页面缓存中的temporaryCash, 不然用户可能点击添加现金进入现金填写页面, 这时候如果缓存没删除, 现金页面能拿到值
                setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                    ...$innerFormutil.$params
                });
            }
        );
    };

    const handleAddSymbol = (type: SymbolTypeEnum) => {
        const route = SYMBOL_TYPE_ROUTES[type];

        if (route) {
            navigate(route.addPath);
        }
    };

    const handleEditSymbol = (symbol: string, type: SymbolTypeEnum) => {
        const route = SYMBOL_TYPE_ROUTES[type];

        if (route) {
            navigate(route.addPath, {
                state: { isEdit: true, [route.editStateKey]: symbol }
            });
        }
    };
    const handleDeleteSymbol = async (symbol: string, type: SymbolTypeEnum) => {
        const listKeyMap: Record<
            string,
            keyof Pick<PartialDetailsPageFormutil, 'temporaryStockList' | 'temporaryOptionList' | 'temporaryFundList'>
        > = {
            [SymbolTypeEnum.STOCK]: 'temporaryStockList',
            [SymbolTypeEnum.OPTION]: 'temporaryOptionList',
            [SymbolTypeEnum.MUTUAL_FUND]: 'temporaryFundList'
        };

        const listKey = listKeyMap[type];

        if (listKey) {
            const currentList = $params[listKey] || [];

            await $formutil.$setValues({
                [listKey]: currentList.filter(item => normalizeTicker(item.symbol) !== normalizeTicker(symbol))
            });
        }

        /**
         * 删除之后要及时更新页面缓存, 用户进入其他页面的方式不仅限于点击下一步按钮
         * 且需要注意的是, 这里必须要使用最新的$formutil.$new().$params来更新缓存, 因为上面你的操作是$setValues
         */
        setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, $formutil.$new().$params);
    };
    const handleNext = () => {
        onSubmit(() => {
            // 如果现金/股票/期权/基金都没有添加, 就提示用户添加资产
            if ([hasCash, hasStock, hasOption, hasFund].every(i => !i)) {
                Toast.show(__('Please add some assets'));
            } else {
                goNextPath('../preview');
            }
        });
    };

    const renderCash = () => {
        return (
            <>
                {hasCash ? (
                    <div className={style.cashItem}>
                        <InfoTag label={__('Cash amount')} content={`${formatMoney(temporaryCash)} USD`} />
                    </div>
                ) : (
                    <div className={style.addItem} onClick={() => handleAddCash(false)}>
                        <IconAdd className={style.addIcon} />
                        <span className={style.addText}>{__('Add cash')}</span>
                    </div>
                )}
            </>
        );
    };

    const renderSymbolSection = (type: SymbolTypeEnum, label: string, data: typeof temporaryStockList) => (
        <div>
            <div className={style.addItem} onClick={() => handleAddSymbol(type)}>
                <IconAdd className={style.addIcon} />
                <span className={style.addText}>{label}</span>
            </div>
            <SymbolPreviewList
                data={data}
                onEdit={symbol => handleEditSymbol(symbol, type)}
                onDelete={symbol => handleDeleteSymbol(symbol, type)}
            />
        </div>
    );

    useEffect(() => {
        store.setShowProgressBar(!isEditMode);
    }, [isEditMode]);

    return (
        <Card form>
            <ScrollTop />
            <h3 className="transfer-title">{__('Transfer Details')}</h3>
            {showFilter && (
                <div className={style.filter}>
                    <div
                        className={classlist(style.filterItem, { [style.selected]: filterType === 'all' })}
                        onClick={() => setFilterType('all')}>
                        {__('All assets')}
                    </div>
                    {hasCash && (
                        <div
                            className={classlist(style.filterItem, { [style.selected]: filterType === 'cash' })}
                            onClick={() => setFilterType('cash')}>
                            {__('Cash balance')}
                        </div>
                    )}
                    {hasStock && (
                        <div
                            className={classlist(style.filterItem, { [style.selected]: filterType === 'stock' })}
                            onClick={() => setFilterType('stock')}>
                            {__('Stock and ETF')}
                        </div>
                    )}
                    {hasOption && (
                        <div
                            className={classlist(style.filterItem, { [style.selected]: filterType === 'option' })}
                            onClick={() => setFilterType('option')}>
                            {__('Option')}
                        </div>
                    )}
                    {hasFund && (
                        <div
                            className={classlist(style.filterItem, { [style.selected]: filterType === 'fund' })}
                            onClick={() => setFilterType('fund')}>
                            {__('Mutual Funds')}
                        </div>
                    )}
                </div>
            )}
            <EasyField type="hidden" name="temporaryCash" />
            {/* 默认是空数组 */}
            <EasyField type="hidden" name="temporaryStockList" $defaultValue={[]} />
            <EasyField type="hidden" name="temporaryOptionList" $defaultValue={[]} />
            <EasyField type="hidden" name="temporaryFundList" $defaultValue={[]} />
            {(!showFilter || ['all', 'cash'].includes(filterType)) && (
                <div>
                    <div className={style.block}>
                        <div className={style.blockTitle}>
                            <span>{__('Cash balance')}</span>
                            {hasCash && (
                                <div className={style.cashIconContainer}>
                                    <IconDelete className={style.deleteIcon} onClick={handleDeleteCash} />
                                    <IconEdit className={style.editIcon} onClick={() => handleAddCash(true)} />
                                </div>
                            )}
                        </div>
                        <div className={style.blockContent}>{renderCash()}</div>
                    </div>
                </div>
            )}
            {(!showFilter || ['all', 'stock'].includes(filterType)) && (
                <div className="pt-3">
                    <div className={style.block}>
                        <div className={style.blockTitle}>{__('Stocks and ETF')}</div>
                        <div className={style.blockContent}>
                            {renderSymbolSection(SymbolTypeEnum.STOCK, __('Add Stock/ETF'), temporaryStockList)}
                        </div>
                    </div>
                </div>
            )}
            {(!showFilter || ['all', 'option'].includes(filterType)) && (
                <div className="pt-3">
                    <div className={style.block}>
                        <div className={style.blockTitle}>{__('Transfer:Option')}</div>
                        <div className={style.blockContent}>
                            {renderSymbolSection(SymbolTypeEnum.OPTION, __('Add Option'), temporaryOptionList)}
                        </div>
                    </div>
                </div>
            )}
            {(!showFilter || ['all', 'fund'].includes(filterType)) && (
                <div className="pt-3">
                    <div className={style.block}>
                        <div className={style.blockTitle}>{__('Mutual Funds')}</div>
                        <div className={style.blockContent}>
                            {renderSymbolSection(
                                SymbolTypeEnum.MUTUAL_FUND,
                                __('Add Fund Position'),
                                temporaryFundList
                            )}
                        </div>
                    </div>
                </div>
            )}
            <CardButtonWrap className="mt-5">
                <Button size="large" type="primary" onClick={handleNext}>
                    {__('Next')}
                </Button>
            </CardButtonWrap>
        </Card>
    );
};

export default withTransferForm({
    step: 3,
    id: PageIdEnum.PARTIAL_DETAILS,
    basePath: PARTIAL_ACATS_BASE_PATH,
    ensure: [PageIdEnum.BROKER, PageIdEnum.CHECK, PageIdEnum.CHOOSE_TRANSFER_TYPE]
})(PartialDetails);

export { default as FillCash } from './FillCash';
export { default as FillOption } from './FillOption';
export { default as FillStock } from './FillStock';
export { default as FillFund } from './FillFund';
